import { useState } from 'react';
import { CaretUp } from '@phosphor-icons/react';
import clsx from 'clsx';
import { ExternalLinkIcon, formatAsCurrency, Link } from '@la/ds-ui-components';
import { useConsolidatedCheckout } from 'lib/context/ConsolidatedCheckoutContext/ConsolidatedCheckoutContext';
import { getHotelLinks } from 'redux/checkoutSlice';
import { useAppSelector } from 'redux/store';
import {
  BillingSummaryItemLineItem,
  CartItemHotelLink,
  CombinedCartItemData,
  DiscountCode,
} from '../Checkout.types';
import { CheckoutBillingSummaryFees } from '../CheckoutBillingSummary/CheckoutBillingSummaryFees/CheckoutBillingSummaryFees';
import styles from './ConfirmationBillingSummary.module.scss';

export type ConfirmationBillingSummaryProps = {
  showHotelLinks?: boolean;
};

/* ConfirmationBillingSummary */
export default function ConfirmationBillingSummary({
  showHotelLinks = false,
}: Readonly<ConfirmationBillingSummaryProps>) {
  const {
    cartItems,
    taxAmount = '',
    transactionFees,
  } = useConsolidatedCheckout();

  const hotelLinks = useAppSelector(getHotelLinks);

  const hasTaxAmountsOrTransactionFees =
    Number.parseFloat(taxAmount) || Number.parseFloat(transactionFees);

  const hasPaymentOption = cartItems?.some(
    (cartItem) => cartItem.paymentTerm !== 'FULL'
  );

  return (
    <div className={styles.confirmationBillingSummary}>
      <ConfirmationBillingSummaryHeader hasPaymentOption={hasPaymentOption} />
      <hr className={styles.blueBorder} />
      <ConfirmationBillingSummaryDetail
        hotelLinks={showHotelLinks && hotelLinks.length > 1 ? hotelLinks : []}
      />
      {hasTaxAmountsOrTransactionFees ? (
        <hr className={styles.blueBorder} />
      ) : null}
      <CheckoutBillingSummaryFees />
    </div>
  );
}
/* */

/*ConfirmationBillingSummaryHeader */
function ConfirmationBillingSummaryHeader({
  hasPaymentOption,
}: Readonly<{
  hasPaymentOption?: boolean;
}>) {
  const { dueToday, totalAmount } = useConsolidatedCheckout();

  return (
    <div className={styles.confirmationBillingSummaryHeader}>
      <div className={styles.dueTodayRow}>
        <span>Paid today:</span>
        <span>{formatAsCurrency(Number(dueToday))}</span>
      </div>
      {hasPaymentOption ? (
        <div className={styles.totalPriceRow}>
          <span>Total price:</span>
          <span className={styles.totalPrice}>
            {formatAsCurrency(Number(totalAmount))}
          </span>
        </div>
      ) : null}
    </div>
  );
}
/* */

/*ConfirmationBillingSummaryDetail */
function ConfirmationBillingSummaryDetail({
  hotelLinks,
}: Readonly<{
  hotelLinks: CartItemHotelLink[];
}>) {
  const { cartItems } = useConsolidatedCheckout();
  function renderSummaryItems() {
    return cartItems?.map((cartItem: CombinedCartItemData, idx: number) => {
      return (
        <SummaryItem
          {...cartItem}
          key={`${cartItem.cartItemName}-${idx}`}
          hotelLink={hotelLinks[idx]?.hotelLink}
        />
      );
    });
  }
  return <div className={styles.summaryItems}>{renderSummaryItems()}</div>;
}
/* */

function SummaryItem({
  cartItemName,
  description,
  dueToday,
  hotelLink,
  lineItems,
  paymentTerm,
  selectedPaymentPlan,
  totalAmount,
}: Readonly<CombinedCartItemData & { hotelLink?: string }>) {
  const [isPaymentPlanExpanded, setIsPaymentPlanExpanded] = useState(false);

  const handleTogglePaymentPlan = () => {
    setIsPaymentPlanExpanded(!isPaymentPlanExpanded);
  };

  function formatDiscountValue(discount: DiscountCode): string {
    return 'percentage' in discount && discount.percentage != null
      ? `${discount.percentage}%`
      : formatAsCurrency(Number(discount.fixedAmount));
  }

  function extractDiscountCodesFromLineItems(
    lineItems?: BillingSummaryItemLineItem[]
  ): string | null {
    // Early returns for invalid inputs
    if (!lineItems?.length || !lineItems[0]?.discountCodes?.length) {
      return null;
    }

    try {
      const firstDiscount = lineItems[0].discountCodes[0];
      const codeName = firstDiscount.code;
      const value = formatDiscountValue(firstDiscount);

      return `"${codeName}" - ${value} discount`;
    } catch (error) {
      console.error('Error processing discount code:', error);
      return null;
    }
  }

  const discountCodeLineItem = extractDiscountCodesFromLineItems(lineItems);

  return (
    <div className={styles.summaryItem}>
      <div className={styles.confirmationBillingSummaryPrimaryRow}>
        <span>{cartItemName}:</span>
        <div className={styles.paymentLineItem}>
          <PaymentsLineItem
            dueToday={dueToday}
            paymentTerm={paymentTerm}
            totalAmount={totalAmount}
          />
          {selectedPaymentPlan?.name ? (
            <div
              className={clsx(styles.paymentPlanToggle, {
                [styles.paymentPlanToggleExpanded]: isPaymentPlanExpanded,
              })}
              onClick={handleTogglePaymentPlan}
            >
              <CaretUp size={16} weight="bold" fill="var(--primary-500)" />
            </div>
          ) : null}
        </div>
      </div>
      <div className={styles.confirmationBillingSummarySecondaryRow}>
        <span>{description}</span>
        {isPaymentPlanExpanded && selectedPaymentPlan?.name ? (
          <div>
            <span>{selectedPaymentPlan.name}</span>
          </div>
        ) : null}
      </div>
      {discountCodeLineItem ? (
        <div className={styles.confirmationBillingSummarySecondaryRow}>
          <span></span>
          <span>{discountCodeLineItem}</span>
        </div>
      ) : null}
      {hotelLink ? (
        <div className={styles.confirmationBillingSummaryTertiaryRow}>
          <Link href={hotelLink} isBold variant="text" target="_blank">
            Manage accommodations
            <ExternalLinkIcon
              fill="var(--secondary-600)"
              variant="bold"
              size="large"
            />
          </Link>
        </div>
      ) : null}
    </div>
  );
}

function PaymentsLineItem({
  dueToday,
  paymentTerm,
  totalAmount,
}: Readonly<{
  dueToday: string;
  paymentTerm?: string;
  totalAmount: string;
}>) {
  const dueTodayAmount =
    paymentTerm === 'PAYMENT_PLAN' && dueToday !== totalAmount ? (
      <>{formatAsCurrency(Number(dueToday))} / </>
    ) : null;
  return (
    <span className={styles.costContainer}>
      {dueTodayAmount}
      <span className={styles.totalCost}>
        {formatAsCurrency(Number(totalAmount))}
      </span>
    </span>
  );
}
